import { usePage } from '@inertiajs/react'
import { Fragment, useEffect } from 'react'

import { Container, Stack, Grid, Group, Title, Text } from '@mantine/core'
import StationMapPanel from '../_StationMapPanel'
import Insights from '../_Insights'
import AppPromo from '../../_AppPromo'
import Breadcrumb from '../_Breadcrumb'
import ChartsCard from '../_ChartsCard'
import Credits from '../_Credits'
import FavoriteButton from '../_FavoriteButton'
import { locationLine, direction } from '../_format'
import { NowTile, DirLine, TILE } from '../_tiles'
import { WeekCard, WeekSection, WeekRow, weekDate } from '../_week'
import { dayConditionGlyph } from '../_weather/ConditionGlyph'
import { useWeather } from '../_weather/useWeather'
import { alignDaily, groupHourlyByLocalDay } from '../_weather/weatherData'
import ConditionsSummary from '../_weather/ConditionsSummary'
import MarineForecastRail from '../_weather/MarineForecastRail'
import DayWeatherBand from '../_weather/DayWeatherBand'
import DayWeatherBandSkeleton from '../_weather/DayWeatherBandSkeleton'
import { useFormat } from '../../_units'
import { useStations } from '../../_stations'
import { useI18n } from '../../_i18n'
import Seo from '../../_Seo'
import { stationCrumbs, stationPlace, breadcrumbList } from '../../_structuredData'
import { useLocalePath } from '../../_localePath'
import { useStationTimezoneMatch } from '../_stationTimezoneMatch'

// Server-rendered current (tidal current) station page. Receives the exact v2
// API payload as `current_station` plus a small `seo` prop (parent permalinks).
// Renders a breadcrumb, name, last/next/following current, and the week —
// backed by matching JSON-LD.

export default function CurrentStationPage({
  current_station,
  weather_panel = false,
  layer_coverage: layerCoverage = null,
  marine_conditions = null,
  station_now = null,
  timezone_abbr: timezoneAbbr = null,
  charts = [],
  seo = {},
}) {
  const s = current_station || {}
  // The head script in layouts/_station_timezone only runs on a hard
  // load/reload; this covers Inertia navigating here client-side — see
  // _stationTimezoneMatch for why both are needed.
  useStationTimezoneMatch(s.timezone)
  const { t, locale } = useI18n()
  const title = t('current_title', { name: s.name })
  const location = locationLine(s)
  const days = Object.entries(s.next_seven_days || {})
  const { site_url: siteUrl = '', canonical_url: canonicalUrl = '' } = usePage().props

  const fmt = useFormat()
  const localePath = useLocalePath()
  const crumbs = stationCrumbs(s, seo, localePath)

  // Client-fetched from the marine-weather CDN (never server-side — no live API
  // calls during a request). Renders nothing when gated off or the station
  // isn't in the feed's registry.
  const weather = useWeather('current', s.id, weather_panel)
  // Aligned to the same `days` keys as the payload's next_seven_days, so each
  // day's Weather band can sit right beside that day's Currents band.
  const weatherDaily =
    weather.status === 'ready' && weather.data
      ? alignDaily(
          weather.data.daily,
          days.map(([date]) => date),
        )
      : []
  const weatherHourlyByDay =
    weather.status === 'ready' && weather.data
      ? groupHourlyByLocalDay(weather.data.hourly, s.timezone)
      : {}
  // Only when the feed says something is active: a quiet or absent warning feed
  // must never render as an all-clear, here or anywhere else on the page.
  const weatherWarnings =
    weather.status === 'ready' && weather.data?.warnings?.status === 'active'
      ? weather.data.warnings.items || []
      : []

  // Record this current station as a recent on visit (client-only; name-guarded).
  const { recordRecent } = useStations()
  useEffect(() => {
    if (s.id != null && s.name) recordRecent('current', { id: s.id, name: s.name })
  }, [s.id])

  return (
    <>
      <Seo
        title={title}
        description={t('current_seo_description', {
          name: s.name,
          location: location ? ` - ${location}` : '',
        })}
        jsonLd={[stationPlace(canonicalUrl, s), breadcrumbList(siteUrl, crumbs)]}
      />

      <Container size="lg" py="xl">
        <Stack gap="lg">
          <div>
            <Breadcrumb crumbs={crumbs} />
            <Group gap="xs" wrap="nowrap" align="center">
              <Title order={1}>{title}</Title>
              <FavoriteButton kind="current" item={{ id: s.id, name: s.name }} />
            </Group>
          </div>

          {/* Server-rendered from the ingested slice, so it is in the HTML —
            unlike the panels below, which the browser fetches after hydration.
            Any active warnings become the head of this same card once that
            fetch lands; there is no separate banner. */}
          <ConditionsSummary
            conditions={marine_conditions}
            now={station_now}
            station={s}
            kind="current"
            timezone={s.timezone}
            weather={weather}
          />

          <Grid gutter="lg">
            {/* Left ~2/3: Now up top, then the week (no chart — currents have
                no charts tab in the app). */}
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Stack gap="lg">
                <Stack gap="xs">
                  <CurrentNowCard
                    label={t('last_current')}
                    event={s.last_current}
                    bg={TILE.bright}
                    dirs={s}
                    timezoneAbbr={timezoneAbbr}
                  />
                  <CurrentNowCard
                    label={t('next_current')}
                    event={s.next_current}
                    bg={TILE.mid}
                    dirs={s}
                    timezoneAbbr={timezoneAbbr}
                  />
                  <AppPromo variant="horizontal" placement="current-now" />
                  <CurrentNowCard
                    label={t('following_current')}
                    event={s.following_current}
                    bg={TILE.dark}
                    dirs={s}
                    timezoneAbbr={timezoneAbbr}
                  />
                </Stack>

                <Stack gap="lg">
                  {days.map(([date, day], i) => {
                    const dayBlock = (
                      <Stack gap="xs">
                        <Text ta="center" fw={700} fz="1.2rem">
                          {weekDate(t, locale, date, i)}
                        </Text>
                        <WeekCard>
                          <WeekSection bg={TILE.bright} emoji="🌊" title={t('currents')}>
                            {(day.currents || []).map((e, j) => (
                              <WeekRow
                                key={j}
                                left={
                                  <Text fz="1.05rem" fw={600} style={{ color: '#fff' }}>
                                    {direction(t, e.max_velocity)}
                                  </Text>
                                }
                                time={fmt.time(e.date_time, s.timezone)}
                                right={
                                  <DirLine
                                    deg={
                                      e.max_velocity < 0
                                        ? s.ebb_dir
                                        : e.max_velocity > 0
                                          ? s.flood_dir
                                          : null
                                    }
                                    speed={fmt.speed(e.max_velocity)}
                                    color="rgba(255,255,255,0.95)"
                                    fz="1.05rem"
                                    arrow={18}
                                  />
                                }
                              />
                            ))}
                          </WeekSection>
                          {/* The day's condition icon lives here in the header,
                            not in the band body — one icon per day. */}
                          {weatherDaily[i] ? (
                            <WeekSection
                              bg={TILE.mid}
                              emoji={dayConditionGlyph(weatherDaily[i])}
                              title={t('weather')}
                            >
                              <DayWeatherBand
                                day={weatherDaily[i]}
                                hours={weatherHourlyByDay[date] || []}
                                timezone={s.timezone}
                                date={date}
                                warnings={weatherWarnings}
                              />
                            </WeekSection>
                          ) : (
                            weather.status === 'loading' && <DayWeatherBandSkeleton />
                          )}
                        </WeekCard>
                      </Stack>
                    )
                    // One horizontal ad between today and tomorrow (only when
                    // there's a tomorrow to sit before).
                    if (i === 0 && days.length > 1) {
                      return (
                        <Fragment key={date}>
                          {dayBlock}
                          <AppPromo variant="horizontal" placement="current-week" />
                        </Fragment>
                      )
                    }
                    return <Fragment key={date}>{dayBlock}</Fragment>
                  })}
                </Stack>
              </Stack>
            </Grid.Col>

            {/* Right: Map (square) up top, then weather + Insights (stub). */}
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Stack gap="lg">
                {s.lat != null && s.lng != null && (
                  <StationMapPanel kind="current" station={s} layerCoverage={layerCoverage} />
                )}
                <MarineForecastRail weather={weather} timezone={s.timezone} />
                <Insights
                  kind="current"
                  insights={s.insights}
                  timezone={s.timezone}
                  midSlot={<AppPromo variant="rectangle" placement="current-insights" />}
                />
                {/* Under the map, where the question "what chart is this water
                    on?" comes from. */}
                <ChartsCard charts={charts} stationName={s.name} />
              </Stack>
            </Grid.Col>
          </Grid>

          <Credits items={[s.attribution, ...(weather.data?.attribution || [])]} />
        </Stack>
      </Container>
    </>
  )
}

// App headline "{LABEL} - {FLOOD|EBB|SLACK}" uppercased; the arrow rotates to
// the flow compass bearing (flood_dir/ebb_dir), none for slack. The web keeps
// the speed as a stat line.
function CurrentNowCard({ label, event, dirs, timezoneAbbr, bg }) {
  const fmt = useFormat()
  const { t } = useI18n()
  const type = event
    ? event.max_velocity == null
      ? 'slack'
      : event.max_velocity < 0
        ? 'ebb'
        : 'flood'
    : null
  const headline = event ? `${label} - ${t(type)}` : label
  const bearing = type === 'flood' ? dirs.flood_dir : type === 'ebb' ? dirs.ebb_dir : null
  return (
    <NowTile
      bg={bg}
      headline={headline}
      time={event ? fmt.time(event.timestamp, dirs.timezone) : null}
      iso={event?.timestamp}
      tzAbbr={timezoneAbbr}
      extra={
        event &&
        event.max_velocity != null && (
          <DirLine
            deg={bearing}
            speed={fmt.speed(event.max_velocity)}
            color="rgba(255,255,255,0.9)"
          />
        )
      }
    />
  )
}
